/**
 * accuracyApi.ts
 * Typed fetch helpers for the accuracy-service (its own Cloud Run service
 * since 2026-08-07 — no VM backend dependency).
 *
 * Base URL: REACT_APP_ACCURACY_API_URL; key: REACT_APP_ACCURACY_API_KEY
 * (both injected at build time — never committed).
 *
 * Endpoint: GET /api/accuracy?day=YYYY-MM-DD[&line=S1][&journey_id=...][&slim=1]
 */

const BASE_URL = (process.env.REACT_APP_ACCURACY_API_URL ?? '').replace(/\/$/, '');
const API_KEY = process.env.REACT_APP_ACCURACY_API_KEY ?? '';
const HEADERS = { 'x-api-key': API_KEY };

// ---- Types ----

/** One metrics bucket (a horizon, or the pooled "all"). */
export interface AccuracyCell {
  n:                     number;
  /** Share of samples where the MESSAGE-carried class == actual class (0..1). */
  class_hit_rate:        number | null;
  /** Number of samples whose ACTUAL class was HOCH. */
  n_high_actual:         number;
  /** Share of actual-HOCH samples the message classed NIEDRIG (0..1). */
  high_to_low_miss_rate: number | null;
  /** Mean absolute error on belegung, in persons. */
  mae_persons:           number | null;
  /** Samples joined against the forecaster's prediction archive. */
  n_model:                     number;
  /** Hit rate of the FORECASTER-DELIVERED class over the joined subset. */
  model_class_hit_rate:        number | null;
  /** H→N miss rate of the forecaster-delivered class over the joined subset. */
  model_high_to_low_miss_rate: number | null;
}

/** Keys: "N+1".."N+5" (only horizons with samples) plus "all". */
export type AccuracyNode = Record<string, AccuracyCell>;

export interface AccuracyResponse {
  day:                   string;
  n_journeys:            number;
  n_samples:             number;
  journey_fetch_errors:  number;
  /** 'forecaster_archive' when the model-class source was reachable. */
  model_source?:         string;
  /** Share of samples with a forecaster-delivered class (0..1), null if unavailable. */
  model_class_coverage?: number | null;
  pareto_versions?:      string[];
  overall:               AccuracyNode;
  by_line:               Record<string, AccuracyNode>;
  by_journey:            Record<string, AccuracyNode>;
  by_stop:               Record<string, AccuracyNode>;
  by_model_version:      Record<string, AccuracyNode>;
}

// ---- API call ----

/**
 * Caddy serves index.html for any unmatched path, so a request that never
 * reaches Cloud Run comes back 200 with HTML. `res.ok` is then true, the status
 * branch below never runs, and res.json() dies on "<!DOCTYPE" — surfacing as
 * `Unexpected token '<'`, which says nothing about the actual fault.
 *
 * The actual fault is almost always that REACT_APP_ACCURACY_API_URL was not
 * injected at build time, leaving BASE_URL empty and the fetch relative.
 */
async function parseJson(res: Response, path: string): Promise<any> {
  const type = res.headers.get('content-type') ?? '';
  if (!type.includes('application/json')) {
    throw new Error(
      BASE_URL
        ? `${path}: expected JSON, got "${type || 'no content-type'}"`
        : `${path}: REACT_APP_ACCURACY_API_URL is not set, so the request went to `
          + `the web root and returned the app itself. Set it at build time.`,
    );
  }
  return res.json();
}

export async function fetchAccuracy(params: {
  day:        string;
  line?:      string;
  journeyId?: string;
  /** Drop the (large) by_journey / by_stop payloads. */
  slim?:      boolean;
}): Promise<AccuracyResponse> {
  const q = new URLSearchParams({ day: params.day });
  if (params.line)      q.set('line', params.line);
  if (params.journeyId) q.set('journey_id', params.journeyId);
  if (params.slim)      q.set('slim', '1');

  const res = await fetch(`${BASE_URL}/api/accuracy?${q.toString()}`, { headers: HEADERS });
  if (!res.ok) {
    const detail = await res.text().catch(() => res.statusText);
    throw new Error(`API ${res.status}: ${detail}`);
  }
  return parseJson(res, '/api/accuracy') as Promise<AccuracyResponse>;
}

// ---- Helpers ----

export const HORIZON_KEYS = ['N+1', 'N+2', 'N+3', 'N+4', 'N+5'] as const;

export function pctLabel(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return '—';
  return `${(rate * 100).toFixed(1)}%`;
}

/**
 * The cell's primary hit rate: the forecaster-delivered (model) class when the
 * archive join covered the cell, otherwise the message-carried class.
 */
export function primaryHitRate(cell: AccuracyCell | undefined): {
  rate: number | null; isModel: boolean;
} {
  if (!cell) return { rate: null, isModel: false };
  if (cell.model_class_hit_rate !== null && cell.model_class_hit_rate !== undefined
      && cell.n_model > 0) {
    return { rate: cell.model_class_hit_rate, isModel: true };
  }
  return { rate: cell.class_hit_rate ?? null, isModel: false };
}

// ---- Model-class endpoint (per journey) ----

export interface ModelClassEntry {
  cls:           'NIEDRIG' | 'MITTEL' | 'HOCH';
  seats:         number | null;
  model_version: string | null;
}

export interface ModelClassResponse {
  day:              string;
  journey_id:       string;
  source:           string;
  pareto_versions?: string[];
  /** Keyed "uic|horizon|target_stop_eva". */
  entries:          Record<string, ModelClassEntry>;
}

export async function fetchModelClass(day: string, journeyId: string):
  Promise<ModelClassResponse> {
  const q = new URLSearchParams({ day, journey_id: journeyId });
  const res = await fetch(`${BASE_URL}/api/model-class?${q.toString()}`, { headers: HEADERS });
  if (!res.ok) {
    const detail = await res.text().catch(() => res.statusText);
    throw new Error(`API ${res.status}: ${detail}`);
  }
  return parseJson(res, '/api/model-class') as Promise<ModelClassResponse>;
}
